import React, { useEffect, useState } from "react";
import { client } from "../config/prismic";

const CONFERENCE_FUNDAMENTALS = [
  {
    title: "Advancements in Intelligent Computing",
    items: [
      {
        label: "Green AI",
        content:
          "Carbon footprint-focused architectures and sustainable modeling.",
      },
      {
        label: "Privacy-First AI",
        content: "Networked and federated learning that protects user data.",
      },
      {
        label: "Edge Intelligence",
        content:
          "High-performance computing for resource-limited environments.",
      },
    ],
  },
  {
    title: "Systems Engineering & High-Assurance",
    items: [
      {
        label: "Intelligent Urban Grids",
        content: "Focus on interoperability and resilient systems.",
      },
      {
        label: "Cyber-Physical Systems (CPS)",
        content:
          "Designing dependable, self-repairing hardware-software ecosystems.",
      },
    ],
  },
  {
    title: "Governance and Security",
    items: [
      {
        label: "Decentralized Identity",
        content:
          "Frameworks for decentralized identifiers and data sovereignty.",
      },
      {
        label: "Open Algorithms",
        content:
          "Promoting transparency through open-source algorithmic governance.",
      },
      {
        label: "Autonomous Frameworks",
        content: "Technology for self-governing systems.",
      },
    ],
  },
  {
    title: "Socio-Technical Dynamics",
    items: [
      {
        label: "Policy-Integrated Design",
        content:
          "Technical systems developed to conform with international policy framework.",
      },
      {
        label: "Human-Centered Computing",
        content: "Promoting user experience and ethical responsibility.",
      },
      {
        label: "Digital Inclusion",
        content:
          "Frameworks designed to ensure equitable access to intelligent systems.",
      },
    ],
  },
];

const About = ({ home }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await client.getByType("about");
        if (response.results[0]) {
          setData(response.results[0].data);
        }
      } catch (error) {
        console.error("Error fetching about data:", error);
      }
    };
    fetchData();
  }, []);

  if (!data) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
      </div>
    );
  }

  // Filter introduction text from Prismic (ignoring fundamentals-related content)
  const introduction = data.description
    .map((para) => para.text.trim())
    .filter((text) => {
      if (!text) return false;
      const lower = text.toLowerCase();
      // Skip the fundamentals section if it exists in Prismic to avoid duplicates
      return (
        !lower.includes("conference fundamentals") &&
        !lower.includes("advancements in intelligent") &&
        !/^(\d+)[.)]/.test(text) &&
        !text.includes(":")
      );
    });

  return (
    <section
      id="rs-about"
      className="relative py-20 overflow-hidden bg-white"
    >
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-pink/5 rounded-full blur-[120px] -z-0"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/5 rounded-full blur-[120px] -z-0"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center mb-24 text-center">
          <div className="w-full max-w-4xl space-y-8">
            <div className="space-y-4">
              <span className="text-pink font-bold tracking-[0.2em] uppercase text-sm inline-block px-4 py-1 border border-pink/30 rounded-full bg-pink/5">
                About The Conference
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-4xl font-extrabold text-slate-900 leading-tight tracking-tight">
                {data.title[0]?.text || "ICSEGS 2026"}
              </h2>
            </div>

            <div className="space-y-8 max-w-3xl mx-auto">
              {introduction.map((text, idx) => (
                <p
                  key={idx}
                  className="text-lg md:text-xl text-slate-600 leading-relaxed font-medium"
                >
                  {text}
                </p>
              ))}
            </div>

            <div className="p-8 rounded-3xl bg-slate-50 border border-slate-200 border-l-4 border-l-pink shadow-sm max-w-3xl mx-auto mt-12">
              <p className="text-slate-500 italic leading-relaxed m-0 text-sm">
                The Microsoft CMT service was used for managing the
                peer-reviewing process for this conference. This service was
                provided for free by Microsoft and they bore all expenses,
                including costs for Azure cloud services as well as for software
                development and support.
              </p>
            </div>
          </div>
        </div>

        {/* Fundamentals Section */}
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h3 className="text-2xl md:text-2xl font-bold text-slate-900 uppercase tracking-wider">
              Conference Fundamentals
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-pink to-purple-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
            {CONFERENCE_FUNDAMENTALS.map((category, idx) => (
              <div
                key={idx}
                className="premium-card bg-white p-12 rounded-[2.5rem] flex flex-col h-full border border-slate-100 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.1)] hover:shadow-[0_25px_50px_-20px_rgba(0,0,0,0.1)] group relative overflow-hidden"
              >
                {/* Numeric Backdrop */}
                <div className="absolute -top-4 -left-2 text-[120px] font-black text-slate-50 leading-none pointer-events-none transition-colors group-hover:text-pink/5">
                  0{idx + 1}
                </div>

                <div className="mb-12 relative z-10 transition-transform group-hover:translate-x-1 duration-500">
                  <h4 className="text-2xl md:text-3xl font-extrabold text-slate-900 m-0 leading-tight tracking-tight">
                    {category.title}
                  </h4>
                </div>
                <ul className="space-y-10 flex-grow relative z-10">
                  {category.items.map((item, iIdx) => (
                    <li key={iIdx} className="relative pl-8 group/item transition-all duration-300">
                      <span className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full bg-slate-200 group-hover/item:bg-pink transition-colors"></span>
                      <span className="block font-bold text-pink/80 mb-2 uppercase tracking-[0.25em] text-[10px]">
                        {item.label}
                      </span>
                      <span className="text-slate-600 font-medium leading-relaxed block text-base md:text-lg group-hover/item:text-slate-900 transition-colors">
                        {item.content}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Venue Section (Refined) */}
        <div className="mt-24 p-10 rounded-3xl bg-slate-50 border border-slate-200 text-center relative overflow-hidden group shadow-lg">
          <div className="absolute inset-0 bg-pink/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative z-10 space-y-4">
            <h2 className="text-2xl font-bold text-gradient uppercase tracking-widest">
              Conference Details
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-3 text-slate-800">
                <i className="fa fa-map-marker text-pink text-2xl"></i>
                <span className="text-xl md:text-2xl font-medium">
                  Venue: {home.location[0]?.text || "TBD"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
